/**
 * PlaceSeasonOverlayEditor – modal editor for a single season overlay.
 * Fields: hero image, summary_sv (0/400), gallery (max 4).
 * Uses the shared MediaImageManager in season mode (same UI as Place → Bilder).
 * Uses unsaved-changes guard.
 */
import { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { FarmlyRichTextField } from '@/components/content/FarmlyRichTextField';
import { Label } from '@/components/ui/label';
import { seasonLabelSv, type Season } from '@/lib/season';
import { upsertOverlay, deleteOverlay, type SeasonOverlay } from '@/lib/api/placeSeasonOverlays';
import { MediaImageManager } from '@/components/media/MediaImageManager';
import { useMediaUpload } from '@/hooks/useMediaUpload';

const MAX_SUMMARY = 5000;
const MAX_DYK = 200;
const MAX_SEASON_GALLERY = 4;

interface Props {
  placeId: string;
  season: Season;
  overlay: SeasonOverlay | null;
  onClose: () => void;
}

export function PlaceSeasonOverlayEditor({ placeId, season, overlay, onClose }: Props) {
  const queryClient = useQueryClient();

  const [summarySv, setSummarySv] = useState(overlay?.summary_sv ?? '');
  const [summaryEn, setSummaryEn] = useState(overlay?.summary_en ?? '');
  const [heroAssetId, setHeroAssetId] = useState<string | null>(overlay?.hero_media_asset_id ?? null);
  const [galleryAssetIds, setGalleryAssetIds] = useState<string[]>(overlay?.gallery_media_asset_ids ?? []);
  const [didYouKnowSv, setDidYouKnowSv] = useState(overlay?.did_you_know_sv ?? '');
  const [didYouKnowEn, setDidYouKnowEn] = useState(overlay?.did_you_know_en ?? '');
  const [typicalDuration, setTypicalDuration] = useState(overlay?.typical_duration ?? '');
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Upload hooks for season mode – uploads will get season_key set
  const coverUpload = useMediaUpload({
    ownerType: 'place',
    ownerId: placeId,
    kind: 'cover',
    maxFiles: 1,
    seasonKey: season,
  });

  const galleryUpload = useMediaUpload({
    ownerType: 'place',
    ownerId: placeId,
    kind: 'gallery',
    maxFiles: MAX_SEASON_GALLERY,
    seasonKey: season,
  });

  // Track dirtiness
  const isDirty =
    summarySv !== (overlay?.summary_sv ?? '') ||
    summaryEn !== (overlay?.summary_en ?? '') ||
    heroAssetId !== (overlay?.hero_media_asset_id ?? null) ||
    JSON.stringify(galleryAssetIds) !== JSON.stringify(overlay?.gallery_media_asset_ids ?? []) ||
    didYouKnowSv !== (overlay?.did_you_know_sv ?? '') ||
    didYouKnowEn !== (overlay?.did_you_know_en ?? '') ||
    typicalDuration !== (overlay?.typical_duration ?? '');

  // Block browser unload when dirty
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    if (isDirty) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const invalidateOverlayQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['season-overlays', placeId] });
    queryClient.invalidateQueries({ queryKey: ['season-overlay', placeId, season] });
    if (heroAssetId) queryClient.invalidateQueries({ queryKey: ['media-asset', heroAssetId] });
    if (galleryAssetIds.length > 0) queryClient.invalidateQueries({ queryKey: ['media-assets-by-ids'] });
    queryClient.invalidateQueries({ queryKey: ['place'] });
    queryClient.invalidateQueries({ queryKey: ['media-assets'] });
  }, [queryClient, placeId, season, heroAssetId, galleryAssetIds]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await upsertOverlay(placeId, season, {
        summary_sv: summarySv || null,
        summary_en: summaryEn || null,
        hero_media_asset_id: heroAssetId,
        gallery_media_asset_ids: galleryAssetIds,
        did_you_know_sv: didYouKnowSv || null,
        did_you_know_en: didYouKnowEn || null,
        typical_duration: typicalDuration || null,
      });
      invalidateOverlayQueries();
      toast.success(`Säsongsöverlagring för ${seasonLabelSv(season)} sparad`);
      onClose();
    } catch (err: any) {
      const msg = err?.message || err?.details || 'Okänt fel';
      toast.error('Kunde inte spara', { description: msg });
      console.error('[PlaceSeasonOverlayEditor] save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [placeId, season, summarySv, heroAssetId, galleryAssetIds, invalidateOverlayQueries, onClose]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    try {
      await deleteOverlay(placeId, season);
      invalidateOverlayQueries();
      toast.success(`Säsongsöverlagring för ${seasonLabelSv(season)} borttagen`);
      onClose();
    } catch (err) {
      toast.error('Kunde inte ta bort');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, [placeId, season, invalidateOverlayQueries, onClose]);

  const handleCoverChange = useCallback((assetId: string | null) => {
    setHeroAssetId(assetId);
  }, []);

  const handleGalleryChange = useCallback((assetIds: string[]) => {
    setGalleryAssetIds(assetIds);
  }, []);

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-display">
              {seasonLabelSv(season)} – säsongsöverlagring
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Beskriv hur platsen känns under {seasonLabelSv(season).toLowerCase()}en.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Shared media uploader in season mode */}
            <MediaImageManager
              ownerType="place"
              ownerId={placeId}
              draftSessionId=""
              coverUpload={coverUpload}
              galleryUpload={galleryUpload}
              disabled={saving}
              seasonKey={season}
              maxGalleryOverride={MAX_SEASON_GALLERY}
              onCoverChange={handleCoverChange}
              onGalleryChange={handleGalleryChange}
              sectionLabel={`Bilder · ${seasonLabelSv(season)}`}
              helperText={`Omslag + galleri (max ${MAX_SEASON_GALLERY}). Bilder sparas automatiskt.`}
            />

            {/* Summary SV */}
            <FarmlyRichTextField
              id="season-summary-sv"
              label="Kort sammanfattning (svenska)"
              value={summarySv}
              onChange={(v) => setSummarySv(v)}
              maxLength={MAX_SUMMARY}
              placeholder={`Hur platsen känns under ${seasonLabelSv(season).toLowerCase()}en…`}
            />

            {/* Summary EN */}
            <FarmlyRichTextField
              id="season-summary-en"
              label="Short summary (English)"
              value={summaryEn}
              onChange={(v) => setSummaryEn(v)}
              maxLength={MAX_SUMMARY}
              placeholder={`How the place feels during ${season}…`}
            />

            {/* Did you know SV */}
            <div className="space-y-1.5">
              <Label htmlFor="season-dyk-sv" className="text-sm font-medium">
                Visste du att… <span className="text-muted-foreground font-normal">(svenska, valfritt)</span>
              </Label>
              <Input
                id="season-dyk-sv"
                value={didYouKnowSv}
                onChange={(e) => setDidYouKnowSv(e.target.value)}
                maxLength={MAX_DYK}
                placeholder="Kort fun-fact om platsen under denna säsong…"
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                {didYouKnowSv.length}/{MAX_DYK} · Tomt = visar default
              </p>
            </div>

            {/* Did you know EN */}
            <div className="space-y-1.5">
              <Label htmlFor="season-dyk-en" className="text-sm font-medium">
                Did you know… <span className="text-muted-foreground font-normal">(English, optional)</span>
              </Label>
              <Input
                id="season-dyk-en"
                value={didYouKnowEn}
                onChange={(e) => setDidYouKnowEn(e.target.value)}
                maxLength={MAX_DYK}
                placeholder="Short fun-fact about the place during this season…"
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                {didYouKnowEn.length}/{MAX_DYK} · Empty = falls back to Swedish
              </p>
            </div>

            {/* Typical duration */}
            <div className="space-y-1.5">
              <Label htmlFor="season-duration" className="text-sm font-medium">
                Vanlig tidsåtgång <span className="text-muted-foreground font-normal">(valfritt)</span>
              </Label>
              <Input
                id="season-duration"
                value={typicalDuration}
                onChange={(e) => setTypicalDuration(e.target.value)}
                placeholder="t.ex. half_day, full_day, 2h"
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Tomt = visar default tidsåtgång
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {overlay && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                Ta bort
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={saving}>
                Avbryt
              </Button>
              <Button type="button" size="sm" onClick={handleSave} disabled={saving || !isDirty}>
                {saving ? 'Sparar…' : 'Spara'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes confirm */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Osparade ändringar</AlertDialogTitle>
            <AlertDialogDescription>
              Du har osparade ändringar. Vill du lämna utan att spara?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stanna kvar</AlertDialogCancel>
            <AlertDialogAction onClick={onClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Lämna
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort säsongsöverlagring?</AlertDialogTitle>
            <AlertDialogDescription>
              Säsongsinnehållet för {seasonLabelSv(season)} tas bort permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
